<template lang="pug">
AppWizardStep(:is-last-step="isLastStep")
  // eslint-disable vue/no-mutating-props
  //- Remove the footer, the wizard will handle it.
  template(#footer)
    .hidden
  .row
    .col-sm-12
      ValidationProvider(
        v-slot="{ invalid, errors }"
        slim
        vid="jobSiteName"
        :rules="jobSiteNameValidation"
        :model-value="value.name"
        :custom-messages="{ notIn: 'Job site with the same name already exists' }"
      )
        .form-group(:class="{ 'has-error': invalid && errors.length }")
          label.control-label.required(for="jobSiteName")
            | Name
            abbr(title="required") *
          AppInput#jobSiteName(
            ref="jobSiteNameInput"
            v-model="value.name"
            type="text"
            @blur="onNameBlur"
          )
          span.help-block(v-if="invalid && errors.length") {{ errors[0] }}
      ValidationProvider(
        ref="addressValidator"
        v-slot="{ invalid, errors }"
        slim
        rules="required"
        vid="jobSiteAddress"
        :model-value="value.address"
      )
        .form-group(:class="{ 'has-error': invalid && errors.length }")
          label.control-label.required(for="jobSiteAddress")
            | Address
            abbr(title="required") *
          GmapAutocomplete#jobSiteAddress.form-control(
            :value="value.address"
            select-first-on-enter
            placeholder="Search for an address"
            @input="onAddressInput"
            @blur="onAddressBlur"
            @place_changed="onAddressChange"
          )
          span.help-block(v-if="invalid && errors.length") {{ errors[0] }}
      .form-group
        .d-flex.justify-content-between
          label.control-label(for="jobSiteRadius") Radius
          small {{ value.radius }} meters
        AppRangeSlider#jobSiteRadius(
          v-model.number="value.radius"
          :step="10"
          :max="3000"
          :min="minimumRadius"
          @input="$emit('input', value)"
        )
        JobSiteSmallRadiusWarning.mt-15(:radius="value.radius")

      //- Map
      JobSitesMap(
        :markers="markers"
        :position="currentPosition"
        :zoom-level="zoomLevel"
        :target-job-site="active ? currentPositionWithRadius : null"
        @click="onNewMarkerPosition"
        @center-changed="onSetCurrentPosition"
        @bounds-changed="bounds = $event"
        @set-current-marker="onNewMarkerPosition"
      )
        AppMarker(
          v-if="jobSitePosition"
          :marker="value"
          has-icon
          is-draggable
          show-marker-radius
          :icon-size="markerIconSize"
          :icon-url="generateCustomMarker(value)"
          :circle-options="markerCircleOptions(value)"
          @update="onNewMarkerPosition"
          @cancel="onCancelJobPosition"
        )
</template>

<script>
import { debounce, isEqual } from 'lodash-es';
import { mapActions, mapGetters } from 'vuex';
import AppMarker from '../AppMapMarker.vue';
import JobSitesMap from './JobSitesMap.vue';
import JobSiteSmallRadiusWarning from './JobSiteSmallRadiusWarning.vue';
import GoogleMaps from '~/mixins/JobSites/GoogleMaps';
import generateCustomColorMarker from '~/utils/generate_custom_color_marker';

export default {
  name: 'NewJobSiteWizardBaseStep',
  components: {
    AppMarker,
    JobSitesMap,
    JobSiteSmallRadiusWarning,
  },
  mixins: [GoogleMaps],
  props: {
    active: {
      type: Boolean,
      required: true,
    },
    editing: {
      type: Boolean,
      default: false,
    },
    isLastStep: {
      type: Boolean,
      default: false,
    },
    value: {
      type: Object,
      required: true,
    },
  },
  emits: ['input'],
  data() {
    return {
      bounds: null,
      ready: false,
      selectedAddress: null,
      geolocatedPosition: null,
      processingAddressChange: false,
      markerIconSize: Object.freeze({
        width: 44,
        height: 54,
      }),
    };
  },
  computed: {
    ...mapGetters([
      'jobSites',
      // We don't really need the markers for anything
      // except to display the map centered around the
      // existing markers/job sites for the user. So
      // that we offer better context.
      'markers',
      'minimumRadius',
      'defaultRadius',
      'mapCenterPosition',
    ]),
    jobSiteNameValidation() {
      return {
        required: true,
        notIn: {
          list: this.jobSites.filter((jobSite) => jobSite.id !== this.value.id).map((jobSite) => jobSite.name),
        },
      };
    },
    zoomLevel() {
      return !this.markers || this.markers.length === 0 ? 1 : 15;
    },
    currentPosition() {
      return this.geolocatedPosition || this.jobSitePosition || this.mapCenterPosition;
    },
    jobSitePosition() {
      const { position, latitude, longitude } = this.value;

      if (latitude && longitude) {
        return {
          lat: parseFloat(latitude),
          lng: parseFloat(longitude),
        };
      }

      return position || null;
    },
    currentPositionWithRadius() {
      // This is a little trick to make Vue track the change and re-run this
      // when the `ready` flag is set to true. Which will make sure that the
      // map is properly zoomed when opening the wizard from a selected
      // marker in the main map.
      if (this.ready) {
        /* empty */
      }

      const { lat, lng } = this.currentPosition || {};

      return {
        latitude: lat,
        longitude: lng,
        radius: this.value.radius || this.defaultRadius,
      };
    },
  },
  watch: {
    // Small little helper to manage focus state
    active: {
      immediate: true,
      async handler(isActive) {
        await this.$nextTick();

        if (isActive && this.$refs.jobSiteNameInput) {
          this.$refs.jobSiteNameInput.focus();
        }
      },
    },
    // One of the requirements, is to make the main map and the wizard map be
    // in sync with each other.
    //
    // To accomplish it, watch the computed property `currentPositionWithRadius`
    // and trigger the `setMapCenterPosition` action with the new value.
    currentPositionWithRadius: {
      async handler(newValue = {}, oldValue = {}) {
        // Only check for the properties that should be synced
        if (['position', 'radius', 'latitude', 'longitude'].some((prop) => !isEqual(newValue[prop], oldValue[prop]))) {
          // Because the main map's `target-job-site` is set to `currentJobSite`
          // this will reset the map center as well as adapt the zoom level.
          this.setCurrentJobSite({ site: newValue });
          // Let the map "catch up" and render the new `position` & `radius`
          await this.$nextTick();
          // Reset the current job site again.
          this.setCurrentJobSite({ site: {} });
        }
      },
    },
  },
  created() {
    // This will help us account for the time that the `place_changed` event
    // is triggered and the native `blur` is triggered.
    this.onAddressBlur = debounce(this.onAddressBlur.bind(this), 200);
  },
  async mounted() {
    // Little trick for the `currentPositionWithRadius` getter to re-run.
    // Read below to understand why and how it works.
    await this.$nextTick();
    this.ready = true;

    // If we open the dialog in `editing` mode assume that the
    // address ir correct since we were able to save the job
    // site in the first place.
    // Also, if we're creating but the user has already geolocated
    // the address, we'll use the geolocated address so assume
    // that the address is correct.
    if (this.editing || (!this.editing && !!this.value.address)) {
      // Make sure that we initialize the `selectedAddress` with the current
      // value of the `value.address` property.
      this.selectedAddress = this.value.address;
      this.$refs.addressValidator.setErrors([]);
      // We need to manually set this because, the Google maps input
      // doesn't trigger the `input` or something, thus the
      // `ValidatorProvider` can't pick it up.
      this.$refs.addressValidator.setFlags({ invalid: false, valid: true });
    }
  },
  methods: {
    ...mapActions(['setCurrentJobSite', 'setMapCenterPosition']),
    markerCircleOptions(marker) {
      return { fillColor: marker.color, strokeColor: marker.color, fillOpacity: 0.3 };
    },
    generateCustomMarker(marker) {
      return generateCustomColorMarker(marker.color);
    },
    onNameBlur() {
      // Remove any whitespaces from the name
      if (this.value.name) {
        this.$emit('input', {
          ...this.value,
          name: this.value.name.trim(),
        });
      }
    },
    onAddressInput(event) {
      if (!event.target.value) {
        this.$refs.addressValidator.setErrors(["can't be blank"]);
      }

      this.$emit('input', {
        ...this.value,
        address: event.target.value,
      });
    },
    async onAddressBlur() {
      // Make sure that we're not processing the address change already
      if (this.processingAddressChange) {
        return;
      }

      const newValue = { ...this.value };

      // Make sure that we remove any trailing and leading whitespace
      if (newValue.address) {
        newValue.address = newValue.address.trim();
      }

      // Make sure that the user hasn't removed changed the address
      // by mistake or something.
      if (newValue.address !== this.selectedAddress) {
        this.$refs.addressValidator.setErrors(['incomplete or invalid']);
      } else if (!newValue.address && !this.selectedAddress) {
        this.$refs.addressValidator.setErrors(["can't be blank"]);
      } else {
        this.$refs.addressValidator.setErrors([]);
      }

      this.$emit('input', newValue);
    },
    onAddressChange(place) {
      this.processingAddressChange = true;
      if (place && place.geometry) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();

        this.selectedAddress = place.formatted_address;
        this.geolocatedPosition = null;

        this.$emit('input', {
          ...this.value,
          latitude: lat,
          longitude: lng,
          position: { lat, lng },
          radius: this.defaultRadius,
          address: place.formatted_address,
        });
        // Commit empty errors to reset the state of the `ValidationProvider`
        this.$refs.addressValidator.setErrors([]);
      } else {
        // Manually set the `ValidationProvider` error state
        this.$refs.addressValidator.setErrors(['Invalid address']);
      }

      this.processingAddressChange = false;
    },
    onCancelJobPosition() {
      this.$emit('input', {
        ...this.value,
        position: null,
        zoomLevel: null,
      });
    },
    async onNewMarkerPosition(event) {
      const lat = event.latLng ? event.latLng.lat() : event.lat;
      const lng = event.latLng ? event.latLng.lng() : event.lng;
      const newValue = {
        ...this.value,
        latitude: lat,
        longitude: lng,
        position: { lat, lng },
        placeId: event.placeId,
        radius: this.defaultRadius,
      };

      this.geolocatedPosition = null;

      const data = await this.getAddressFromMarker(newValue);

      if (data && data.formatted_address) {
        newValue.address = this.selectedAddress = data.formatted_address;
        // Reset the internal validator state
        this.$refs.addressValidator.setErrors([]);
      }

      // We check whether the site where the user clicked has an associated
      // placeId, meaning that it might be a POI or some registered place in Google maps.
      // If it has a placeId attached to the place, we go ahead and fetch the name for it,
      // otherwise we don't.
      // For better UX, it doesn't attempt to update the job site name automatically
      // when _editing_ the location.
      const placeId = newValue.placeId || data.place_id;

      if (placeId && !this.editing) {
        const placesResponse = await this.getPlaceFromId(placeId);

        newValue.name = placesResponse.name;
      }

      this.$emit('input', newValue);
    },
    onSetCurrentPosition(position) {
      if (position) {
        this.geolocatedPosition = position;
      }
    },
  },
};
</script>
